(ns app.front.view.group.reporting.common
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.form-field-selection :as ff-selection]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.reporting.property-attributes :as reporting-pa]
            [app.front.view.party.common :as party-common]
            [app.i18n.model :as i18n-model]
            [app.reporting.model :as reporting-model]
            [clojure.set :as set]
            [core.db.child-entity :as child]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(def date-range-options
  [:reporting.date-range-options/current-month
   :reporting.date-range-options/previous-week
   :reporting.date-range-options/previous-month
   :reporting.date-range-options/year-to-date
   :reporting.date-range-options/last-year
   :reporting.date-range-options/custom])

(rf/reg-event-fx ::init
  (fn [_ [_ group-id default-date-option]]
    {:dispatch-n [[::fetch-group group-id]
                  [::fetch-reports group-id]
                  [::ff-selection/fetch-form-fields group-id]
                  [::gas/fetch-management-views group-id]
                  [::fetch-group-configs group-id]
                  [::set-date-selector-mode default-date-option]
                  [::set-selected-categories #{}]
                  [::set-selected-permit-types #{}]
                  [::set-selected-party-roles #{}]
                  [::reporting-pa/set-selected-attributes []]]}))

(rf/reg-event-fx ::fetch-group
  (fn [{:keys [db]} [_ group-id]]
    {:comm/command {:command    :group/reporting-data
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::fetch-group.success]}
     :db           (dissoc db ::views)}))

(rf/reg-event-fx ::fetch-reports
  (fn [_ [_ group-id]]
    {:comm/command {:command    :report/list-reports
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::entities/merge-entity-db]}}))

(rf/reg-event-fx ::fetch-group-configs
  (fn [{:keys [db]} [_ group-id]]
    {:comm/command {:command    :report/group-configs-for-reporting
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::fetch-group-configs.success group-id]}
     :db           (assoc-in db [::state :loading-configs?] true)}))

(rf/reg-event-fx ::fetch-group-configs.success
  (fn [{:keys [db]} [_ group-id status resp]]
    {:db       (-> db
                   (assoc-in [::state :configs-in-use group-id] (:configs-in-use resp))
                   (assoc-in [::state :loading-configs?] false))
     :dispatch [::entities/merge-entity-db status (:entities resp)]}))

(rf/reg-sub ::loading-configs?
  :<- [::state]
  :-> :loading-configs?)

(rf/reg-event-fx ::fetch-group.success
  (fn [{:keys [db]} [_ status {:keys [entities features views]}]]
    {:dispatch [::entities/merge-entity-db status (du/entities->entity-db entities)]
     :db       (assoc db
                      ::features features
                      ::show-online-payments? (:online-payment? features)
                      ::views views)}))

(rf/reg-sub ::features
  :-> ::features)

(rf/reg-sub ::reporting-tabs
  :-> ::views)

(rf/reg-sub ::show-online-payments? :-> ::show-online-payments?)

(rf/reg-sub
  ::date-selector-mode
  (fn [db _]
    (get db ::date-selector-mode)))

(rf/reg-sub
  ::report-custom-start-date
  (fn [db _]
    (get db ::report-custom-start-date (date-util/today-start-of-day))))

(rf/reg-sub
  ::report-custom-end-date
  (fn [db _]
    (get db ::report-custom-end-date (date-util/today-end-of-day))))

(defn previous-week-start+end []
  (let [start (date-util/start-of-last-week)]
    [start (date-util/add-days-to-date start 7)]))

(rf/reg-sub ::date-range
  :<- [::date-selector-mode]
  :<- [::report-custom-start-date]
  :<- [::report-custom-end-date]
  (fn [[date-selector-mode custom-start custom-end] _]
    (let [now                    (js/Date.)
          current-year           (.getFullYear now)
          current-month          (.getMonth now)
          start-of-current-month (js/Date. current-year current-month 1)
          start-of-last-month    (js/Date. current-year (- current-month 1) 1)
          end-of-last-month      start-of-current-month
          start-of-current-year  (js/Date. current-year 0 1) ; months start from 0, days from 1
          start-of-last-year     (js/Date. (- current-year 1) 0 1)
          end-of-last-year       start-of-current-year]
      (case date-selector-mode
        :reporting.date-range-options/current-month [start-of-current-month now]
        :reporting.date-range-options/previous-month [start-of-last-month end-of-last-month]
        :reporting.date-range-options/year-to-date [start-of-current-year now]
        :reporting.date-range-options/last-year [start-of-last-year end-of-last-year]
        :reporting.date-range-options/custom [custom-start custom-end]
        :reporting.date-range-options/previous-week (previous-week-start+end)
        [start-of-current-month now]))))

(rf/reg-event-db
  ::select-date
  (fn [db [_ id date]]
    (assoc db id date)))

(rf/reg-event-db
  ::set-date-selector-mode
  (fn [db [_ mode]]
    (assoc db ::date-selector-mode mode)))

(rf/reg-event-db ::set-selected-categories
  (fn [db [_ categories]]
    (assoc db ::selected-categories categories)))

(rf/reg-event-db ::set-selected-permit-types
  (fn [db [_ permit-types]]
    (assoc db ::selected-permit-types (set permit-types))))

(rf/reg-event-db ::set-selected-party-roles
  (fn [db [_ roles]]
    (assoc db ::selected-party-roles roles)))

(rf/reg-sub ::selected-categories
  (fn [db _]
    (get db ::selected-categories #{})))

(rf/reg-sub ::selected-permit-types
  (fn [db _]
    (get db ::selected-permit-types #{})))

(rf/reg-sub ::selected-party-roles
  (fn [db _]
    (get db ::selected-party-roles #{})))

(defn combined-report-data [entities name-fn]
  (->> entities
       (group-by name-fn)
       (map (fn [[data-name data-entities]]
              [data-name (du/entity-id-set data-entities)]))
       (into (sorted-map))))

(defn- category-name-str [category-config lang tr]
  (str (config-model/localized-name category-config lang)
       (when-not (du/is-active? category-config)
         (str " (" (tr [(:domain/state category-config)]) ")"))))

(rf/reg-sub ::combined-permit-types+categories
  :<- [::gas/root-group-configs]
  :<- [::all-categories-in-use]
  :<- [:language]
  :<- [::gas/group-tr]
  (fn [[root-configs categories-in-use lang tr] _]
    (->> root-configs
         (group-by :group.config/target)
         (map (fn [[permit-type-key permit-type-configs]]
                (let [categories          (->> permit-type-configs
                                               (mapcat (child/child-entities-of-type-fn :entity.type/group-config))
                                               (filter #(contains? categories-in-use (:db/id %))))
                      category-name-fn    #(category-name-str % lang tr)
                      combined-categories (combined-report-data categories category-name-fn)]
                  [(tr [permit-type-key]) combined-categories]))))))

(rf/reg-sub ::combined-party-roles
  :<- [::gas/root-group-configs]
  (fn [root-configs _]
    (let [entities (->> root-configs
                        (mapcat :group.config/applicable-party-roles))
          name-fn  party-common/party-role-label]
      (combined-report-data entities name-fn))))

(rf/reg-event-db ::set-category-missing-selected
  (fn [db [_ selected?]]
    (assoc db ::category-missing-selected? selected?)))

(rf/reg-sub ::category-missing-selected?
  (fn [db _]
    (get db ::category-missing-selected?)))

(rf/reg-sub ::selected-optional-fields
  (fn [db] (-> db (::selected-optional-fields #{}))))

(rf/reg-event-db
  ::toggle-selected-optional-field
  (fn [db [_ field selected?]]
    (update db
            ::selected-optional-fields
            (comp set (if selected? conj disj)) field)))

(rf/reg-event-db
  ::set-selected-optional-fields
  (fn [db [_ fields]]
    (assoc db ::selected-optional-fields fields)))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::config-ids-in-use
  :<- [::state]
  :-> :configs-in-use)

(defn date-input [tr id opts]
  [ui/date-picker (merge {:id        (str "report-range-" id)
                          :tr        tr
                          :required? true
                          :value     (<sub [id])
                          :max-date  ((<sub [::date-util/date-fn]) (js/Date.))}
                         opts)])

(defn date-range-selector [tr id date-range-options]
  (let [selected (<sub [::date-selector-mode])]
    [:div.flex.flex-wrap.items-center
     (into (cc/select {:id         (str id "-select")
                       :value      (if (keyword? selected) (name selected) "")
                       :class-name "mr-4 mt-6 mb-2"
                       :on-change  (fn [value]
                                     (let [option-name (futil/tval value)
                                           find-fn     #(when (keyword? %) (= (name %) option-name))
                                           option-kw   (p/find-first date-range-options find-fn)]
                                       (>evt [::set-date-selector-mode option-kw])))
                       :label-text (tr [:reporting/time-range])})
           (for [date-range-option date-range-options]
             (cc/select-item {:text  (tr [date-range-option])
                              :value date-range-option})))
     (when (= selected :reporting.date-range-options/custom)
       [:div.mt-6.flex.flex-wrap.sm:flex-nowrap
        [date-input tr ::report-custom-start-date {:label-loc  :reporting/start-date
                                                   :class-name "mr-4 mb-2"
                                                   :callback   #(>evt [::select-date ::report-custom-start-date (date-util/start-of-day %)])}]
        [date-input tr ::report-custom-end-date {:label-loc  :reporting/end-date
                                                 :class-name "mb-2"
                                                 :callback   #(>evt [::select-date ::report-custom-end-date (date-util/end-of-day %)])
                                                 :min-date   (<sub [::report-custom-start-date])}]])]))

(defn report-type-selector [{:keys [tr label-loc selected-index report-type-options on-change]}]
  (into
    [ui/select-pills {:label-text   (tr [label-loc])
                      :on-change    on-change
                      :value        selected-index
                      :option-class "uppercase"}]
    (for [[index report-type] (map vector (range) report-type-options)]
      {:value index
       :text  (tr [report-type])})))

(defn selector-header [tr title-key on-select-all on-deselect-all]
  [:div.flex.flex-row.items-center.mb-2
   [:h3.m-0 (tr [title-key])]
   [:div.ml-6.mt-1
    (when on-select-all
      (cc/button {:kind :ghost :size :field :on-click on-select-all :class-name "p-2"}
                 (tr [:reporting/select-all])))
    (when on-deselect-all
      (cc/button {:kind :ghost :size :field :on-click on-deselect-all :class-name "p-2"}
                 (tr [:reporting/deselect-all])))]])

(rf/reg-sub ::all-categories-in-use
  (fn [_]
    [(rf/subscribe [::gas/root-group-configs])
     (rf/subscribe [::config-ids-in-use])
     (rf/subscribe [::gas/group-id])])
  (fn [[root-configs ids-in-use group-id]]
    (->> root-configs
         (mapcat :entity/_parent)
         (map :db/id)
         (filter #(contains? (get ids-in-use group-id) %))
         set)))

(defn category-selector [tr include-category-missing?]
  (let [permit-types+categories (<sub [::combined-permit-types+categories])
        multiple-permit-types?  (< 1 (count permit-types+categories))
        selected-categories     (<sub [::selected-categories])
        all-categories-in-use   (<sub [::all-categories-in-use])
        handle-category-change  (fn [selected-categories no-category-checked?]
                                  (if include-category-missing?
                                    #(do (>evt [::set-selected-categories selected-categories])
                                         (>evt [::set-category-missing-selected no-category-checked?]))
                                    #(>evt [::set-selected-categories selected-categories])))
        on-select-all           (handle-category-change all-categories-in-use true)
        on-deselect-all         (handle-category-change #{} false)]
    [:div
     [selector-header tr :reporting/permit-categories on-select-all on-deselect-all]
     (for-frag [[permit-type-name categories] permit-types+categories]
       [:div.mt-4
        (when multiple-permit-types? [:h4.my-2 permit-type-name])
        [:div.flex.flex-row.flex-wrap
         (for-frag [[category-name id-set] categories]
           [:div.w-56
            {:key (str "category-option-" id-set)}
            (cc/checkbox {:id         (str "admin-reports:category" id-set)
                          :label-text category-name
                          :checked    (set/subset? id-set selected-categories)
                          :on-change  #(>evt [::set-selected-categories
                                              ((if % set/union set/difference) selected-categories id-set)])})])
         (when include-category-missing?
           [:div.w-56
            {:key "category-option-undefined"}
            (cc/checkbox {:id         "admin-reports:category-undefined"
                          :label-text (tr [:category/undefined])
                          :checked    (boolean (<sub [::category-missing-selected?]))
                          :on-change  #(>evt [::set-category-missing-selected %])})])]])]))

(defn permit-type-selector [tr]
  (let [permit-types          (<sub [::gas/group-primary-permit-types])
        selected-permit-types (<sub [::selected-permit-types])
        on-select-all         #(>evt [::set-selected-permit-types permit-types])
        on-deselect-all       #(>evt [::set-selected-permit-types #{}])]
    [:div
     [selector-header tr :reporting/permit-type on-select-all on-deselect-all]
     [:div.flex.flex-row.flex-wrap
      (for-frag [permit-type (sort permit-types)]
        [:div.w-56
         {:key (str "permit-type-option-" (name permit-type))}
         (cc/checkbox {:id         (str "admin-reports:permit-type-" (name permit-type))
                       :label-text (tr [permit-type])
                       :checked    (contains? selected-permit-types permit-type)
                       :on-change  #(>evt [::set-selected-permit-types
                                           ((if % conj disj) selected-permit-types permit-type)])})])]]))

(defn- reporting-tab-label [view]
  (case view
    :view/group.reporting.cases :view/group.reporting.cases.title
    :view/group.reporting.statcan-spreadsheet :reporting.statcan/issuance-tab
    view))

(rf/reg-sub ::reporting-tab-items
  :<- [::reporting-tabs]
  (fn [views [_ group-id]]
    (mapv (fn [view]
            {:id           view
             :label-tr-key (reporting-tab-label view)
             :href         (href/href view {:group-id group-id})})
          views)))

(defn report-page [match tr & children]
  (let [ce?      (<sub [::gas/has-permit-type? #{:permit-type/CE}])
        pwo?     (<sub [::gas/has-permit-type? #{:permit-type/PWO}])
        tr       (i18n-model/wrap-tr-fn
                   (i18n-model/code-enforcement-work-order-replacements ce? pwo?)
                   tr)
        group-id (-> match :parameters :path :group-id)
        current  (-> match :data :name)
        tabs     (->> (<sub [::reporting-tab-items group-id])
                      (map (fn [{:keys [id href label-tr-key]}]
                             (util/mmap {:label (tr [label-tr-key])} id href))))]
    [acommon/admin-page-v2
     {:match          match
      :title          (tr [:reporting/title])
      :header-content [ui-tabs/tabs
                       {:selected-id current
                        :aria-label  (tr [:reporting/title])
                        :tabs        tabs}]}
     (into [acommon/section-title-wrapper {:hide-title? true}] children)]))

(defn optional-field-selector [tr report-kw optional-fields]
  (let [selected-fields  (<sub [::selected-optional-fields])
        permit-types     (<sub [::gas/group-permit-types])
        country          (<sub [::front-config/country])
        group            (<sub [::gas/group])
        available-fields (->> optional-fields
                              (remove (partial reporting-model/unavailable-field? country group true permit-types report-kw)))
        on-select-all    #(>evt [::set-selected-optional-fields (set available-fields)])
        on-deselect-all  #(>evt [::set-selected-optional-fields #{}])]
    [:div
     [selector-header tr :reporting/optional-fields on-select-all on-deselect-all]
     (into
       [:div.flex.flex-row.flex-wrap]
       (for [field available-fields]
         [:div.w-56
          (cc/checkbox {:id         (str "admin-reports:field" field)
                        :label-text (tr [field])
                        :checked    (contains? selected-fields field)
                        :on-change  #(>evt [::toggle-selected-optional-field field %])})]))]))

(defn related-parties-selector [tr]
  (let [selected-party-roles (<sub [::selected-party-roles])
        party-roles          (<sub [::combined-party-roles])
        on-select-all        #(>evt [::set-selected-party-roles (apply set/union (vals party-roles))])
        on-deselect-all      #(>evt [::set-selected-party-roles #{}])]
    [:div
     [selector-header tr :reporting/related-party-roles on-select-all on-deselect-all]
     [:div.columns-1.sm:columns-2.md:columns-3
      (for-frag [[name id-set] party-roles]
        [:<>
         {:key (str "party-role-option-" id-set)}
         (cc/checkbox {:id         (str "admin-reports:party-role" id-set)
                       :label-text name
                       :checked    (set/subset? id-set selected-party-roles)
                       :on-change  #(>evt [::set-selected-party-roles
                                           ((if % set/union set/difference) selected-party-roles id-set)])})])]]))

(rf/reg-event-db ::set-selected-states
  (fn [db [_ v]]
    (assoc db ::selected-states v)))

(rf/reg-sub ::selected-states
  #(-> % ::selected-states set))

(defn status-selector [{:keys [tr states header-key]}]
  (let [selected-states (<sub [::selected-states])
        on-select-all   #(>evt [::set-selected-states (set states)])
        on-deselect-all #(>evt [::set-selected-states #{}])]
    [:div
     [selector-header tr header-key on-select-all on-deselect-all]
     [:div.flex.flex-row.flex-wrap
      (for-frag [state states]
        [:div.w-56
         (cc/checkbox {:id         (str state "-checkbox")
                       :label-text (tr [state])
                       :checked    (contains? selected-states state)
                       :on-change  #(>evt [::set-selected-states
                                           ((if % conj disj) selected-states state)])})])]]))

(rf/reg-event-fx ::set-date-format
  (fn [{:keys [db]} [_ date-format]]
    {:db (assoc db ::reporting-date-format date-format)}))

(rf/reg-sub ::reporting-date-format
  (fn [db]
    (get db ::reporting-date-format (first reporting-model/date-format-options))))

(defn date-format-selector
  [tr]
  [:div.w-80
   [ui-select/select
    {:id           "date-format-selector"
     :class        "w-full"
     :values       reporting-model/date-format-options
     :value        (<sub [::reporting-date-format])
     :val->str     #(tr [%])
     :label-text   (tr [:reporting/date-format-selector])
     :on-change    #(>evt [::set-date-format %])
     :allow-empty? false}]])

(defn current-year-month
  ([]
   (current-year-month false))
  ([month-1-based?]
   (let [now (date-util/now)]
     {:year  (.getFullYear now)
      :month (cond-> (.getMonth now)
               month-1-based? inc)})))

(defn month-select-with-button [{:keys [tr title on-change]}
                                children]
  (r/with-let [{:keys [year
                       month]} (current-year-month)
               *selected-year  (r/atom year)
               *selected-month (r/atom month)]
    [:div.w-full.lg:w-1-2.pt-6.lg:p-4
     title
     [:div.flex.flex-wrap
      [:div.w-1-2.pr-2.mt-3
       [ui-select/select {:id           "statcan-report-year"
                          :class-name   "full-width-select"
                          :allow-empty? false
                          :values       (range 2020 (inc year))
                          :value        @*selected-year
                          :label-text   (tr [:date/year])
                          :on-change    (fn [val]
                                          (reset! *selected-year val)
                                          ;; JS months start from zero
                                          (on-change (inc @*selected-month) @*selected-year))}]]
      [:div.w-1-2.pl-2.mt-3
       [ui-select/select {:id           "statcan-report-month"
                          :class-name   "full-width-select"
                          :allow-empty? false
                          :values       (range 12)
                          :value        @*selected-month
                          :val->str     #(tr [(keyword "date.month" (str %))])
                          :label-text   (tr [:date/month])
                          :on-change    (fn [month]
                                          (reset! *selected-month month) ;; JS months start from zero
                                          (on-change (inc @*selected-month) @*selected-year))}]]]
     [:div.flex.w-full.justify-end.mt-3
      children]]))
